import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import GalleryService from '@clickersphere/core/services/GalleryService';
import useToast from '../useToast';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { GALLERIES_QUERY_KEY, GalleryQueryData } from './useGalleries';

export function useRemoveGallery(leadId: string) {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();
  const { toast } = useToast();
  const { t } = useTranslation();

  const { isPending: removeGalleryIsPending, mutateAsync: removeGalleryAsync } =
    useMutation({
      mutationFn: (galleryId: string) => GalleryService.remove(galleryId),
      onMutate: async (galleryId) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey([GALLERIES_QUERY_KEY, leadId]),
        });

        const oldGalleries = queryClient.getQueryData<GalleryQueryData[]>(
          buildQueryKey([GALLERIES_QUERY_KEY, leadId]),
        );

        queryClient.setQueryData<GalleryQueryData[]>(
          buildQueryKey([GALLERIES_QUERY_KEY, leadId]),
          (old) => old?.filter((gallery) => gallery.id !== galleryId),
        );

        return { oldGalleries };
      },
      onSuccess: () => {
        toast(t('gallery.toasts.removed'), 'success');
      },
      onError: (_error, _galleryId, context) => {
        queryClient.setQueryData(
          buildQueryKey([GALLERIES_QUERY_KEY, leadId]),
          context?.oldGalleries,
        );
        toast(t('gallery.toasts.removeError'), 'error');
      },
    });

  return {
    removeGalleryAsync,
    removeGalleryIsPending,
  };
}
